import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

export interface CharacterAutoCompleteStorage {
  suggestions: string[];
  selectedIndex: number;
  active: boolean;
  query: string;
}

// Collect unique character names from the document, sorted by frequency
function collectCharacterNames(doc: any): string[] {
  const freq = new Map<string, number>();

  doc.descendants((node: any) => {
    if (node.type.name === 'characterName' && node.textContent.trim()) {
      const name = node.textContent.trim().toUpperCase();
      freq.set(name, (freq.get(name) || 0) + 1);
    }
  });

  return Array.from(freq.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([name]) => name);
}

export const characterAutoCompletePluginKey = new PluginKey('characterAutoComplete');

export const CharacterAutoComplete = Extension.create({
  name: 'characterAutoComplete',

  addStorage() {
    return {
      suggestions: [] as string[],
      selectedIndex: 0,
      active: false,
      query: '',
      allNames: [] as string[],
      namesCacheVersion: -1,
    };
  },

  addProseMirrorPlugins() {
    const extension = this;

    return [
      new Plugin({
        key: characterAutoCompletePluginKey,
        state: {
          init() {
            return { active: false, suggestions: [], selectedIndex: 0, query: '', decorations: DecorationSet.empty };
          },
          apply(tr, prev, _oldState, newState) {
            // Handle dismiss meta (from Escape key)
            const meta = tr.getMeta(characterAutoCompletePluginKey);
            if (meta?.dismiss) {
              extension.storage.active = false;
              extension.storage.suggestions = [];
              extension.storage.query = '';
              return { active: false, suggestions: [], selectedIndex: 0, query: '', decorations: DecorationSet.empty };
            }

            // Handle selectedIndex change meta (from Arrow keys)
            if (meta?.selectedIndex !== undefined) {
              return { ...prev, selectedIndex: meta.selectedIndex };
            }

            const { $from } = newState.selection;
            const node = $from.parent;

            // Only active inside characterName nodes
            if (node.type.name !== 'characterName') {
              if (prev.active) {
                extension.storage.active = false;
                extension.storage.suggestions = [];
                extension.storage.query = '';
              }
              return { active: false, suggestions: [], selectedIndex: 0, query: '', decorations: DecorationSet.empty };
            }

            const query = node.textContent.trim().toUpperCase();

            // No suggestions for empty input
            if (!query) {
              extension.storage.active = false;
              extension.storage.suggestions = [];
              extension.storage.query = '';
              return { active: false, suggestions: [], selectedIndex: 0, query: '', decorations: DecorationSet.empty };
            }

            // Rebuild name cache if doc changed or query changed
            if (tr.docChanged || prev.query !== query) {
              const allNames = collectCharacterNames(newState.doc);

              // Filter: match prefix, exclude exact match (don't suggest what's already typed)
              const suggestions = allNames
                .filter(name => name.startsWith(query) && name !== query)
                .slice(0, 8);

              const selectedIndex = suggestions.length > 0
                ? Math.min(prev.selectedIndex, suggestions.length - 1)
                : 0;

              const active = suggestions.length > 0;

              extension.storage.active = active;
              extension.storage.suggestions = suggestions;
              extension.storage.selectedIndex = selectedIndex;
              extension.storage.query = query;

              return { active, suggestions, selectedIndex, query, decorations: DecorationSet.empty };
            }

            return prev;
          },
        },
        props: {
          handleKeyDown(view, event) {
            const pluginState = characterAutoCompletePluginKey.getState(view.state);
            // Only intercept keys when suggestions are actually visible
            if (!pluginState?.active || pluginState.suggestions.length === 0) return false;

            const { suggestions } = pluginState;

            if (event.key === 'ArrowDown') {
              event.preventDefault();
              const next = (pluginState.selectedIndex + 1) % suggestions.length;
              extension.storage.selectedIndex = next;
              view.dispatch(view.state.tr.setMeta(characterAutoCompletePluginKey, { selectedIndex: next }));
              return true;
            }

            if (event.key === 'ArrowUp') {
              event.preventDefault();
              const next = (pluginState.selectedIndex - 1 + suggestions.length) % suggestions.length;
              extension.storage.selectedIndex = next;
              view.dispatch(view.state.tr.setMeta(characterAutoCompletePluginKey, { selectedIndex: next }));
              return true;
            }

            // Tab: cycle forward through suggestions
            if (event.key === 'Tab' && !event.shiftKey) {
              event.preventDefault();
              const next = (pluginState.selectedIndex + 1) % suggestions.length;
              extension.storage.selectedIndex = next;
              view.dispatch(view.state.tr.setMeta(characterAutoCompletePluginKey, { selectedIndex: next }));
              return true;
            }

            // Shift+Tab: cycle backward through suggestions
            if (event.key === 'Tab' && event.shiftKey) {
              event.preventDefault();
              const next = (pluginState.selectedIndex - 1 + suggestions.length) % suggestions.length;
              extension.storage.selectedIndex = next;
              view.dispatch(view.state.tr.setMeta(characterAutoCompletePluginKey, { selectedIndex: next }));
              return true;
            }

            // Enter: accept selected suggestion and advance to dialogue
            if (event.key === 'Enter') {
              event.preventDefault();
              const selected = suggestions[pluginState.selectedIndex];
              if (selected) {
                const { $from } = view.state.selection;
                const start = $from.start();
                const end = $from.end();
                const tr = view.state.tr.insertText(selected, start, end);
                view.dispatch(tr);

                // Advance to dialogue on next line
                setTimeout(() => {
                  const editor = extension.editor;
                  if (editor) {
                    editor.chain().splitBlock().setNode('dialogue').run();
                  }
                }, 0);
              }
              return true;
            }

            if (event.key === 'Escape') {
              event.preventDefault();
              extension.storage.active = false;
              extension.storage.suggestions = [];
              view.dispatch(view.state.tr.setMeta(characterAutoCompletePluginKey, { dismiss: true }));
              return true;
            }

            return false;
          },
        },
      }),
    ];
  },
});
